/**
 * Prefetch lazy route chunks on sidebar hover/focus.
 *
 * Uses the same dynamic-import targets as lazyRoutes.ts so a hover warms the
 * exact module React.lazy will request on click. Deduplicates in-flight and
 * completed prefetches. Chunk-only — no data fetching (avoids tenant/cache risk).
 */

type PrefetchFactory = () => Promise<unknown>;

/** Path prefix → import factory (aligned with lazyRoutes). */
const ROUTE_PREFETCHERS: Array<{ prefix: string; load: PrefetchFactory }> = [
  { prefix: '/business', load: () => import('../pages/business/BusinessHub') },
  { prefix: '/studio', load: () => import('../pages/email-marketing/EmailMarketing') },
  { prefix: '/automations', load: () => import('../pages/automations/lab/AutomationsLab') },
  { prefix: '/chat', load: () => import('../pages/buzztalk/BuzzTalk') },
  { prefix: '/calendar', load: () => import('../pages/marketing/SocialAutomator') },
  { prefix: '/marketing/social', load: () => import('../pages/marketing/SocialAutomator') },
  { prefix: '/inbox', load: () => import('../pages/inbox/UnifiedInboxPage') },
  { prefix: '/account', load: () => import('../pages/Settings').then((m) => ({ default: m.Settings })) },
  { prefix: '/popups', load: () => import('../pages/Popups').then((m) => ({ default: m.Popups })) },
];

const warmed = new Set<string>();

/** Strip query/hash so `/studio?tab=my-flows` maps to `/studio`. */
function pathnameOnly(href: string): string {
  const q = href.indexOf('?');
  const h = href.indexOf('#');
  let end = href.length;
  if (q >= 0) end = Math.min(end, q);
  if (h >= 0) end = Math.min(end, h);
  return href.slice(0, end) || '/';
}

function resolvePrefetcher(pathname: string): PrefetchFactory | null {
  // Longest prefix wins (/marketing/social before /marketing if added later).
  let best: { prefix: string; load: PrefetchFactory } | null = null;
  for (const entry of ROUTE_PREFETCHERS) {
    const { prefix } = entry;
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      if (!best || prefix.length > best.prefix.length) best = entry;
    }
  }
  return best?.load ?? null;
}

/** Prefetch the JS chunk for a nav href. Safe to call repeatedly. */
export function prefetchRoute(href: string): void {
  const pathname = pathnameOnly(href);
  const load = resolvePrefetcher(pathname);
  if (!load) return;
  const key = pathname === '/' ? '/' : pathname.replace(/\/+$/, '') || '/';
  // Key by the matched factory identity via prefix from resolve — use pathname
  // of the known sidebar roots so /chat/abc shares /chat's warm.
  const factoryKey = ROUTE_PREFETCHERS.find((e) => {
    const p = e.prefix;
    return key === p || key.startsWith(`${p}/`);
  })?.prefix;
  if (!factoryKey || warmed.has(factoryKey)) return;
  warmed.add(factoryKey);
  void load().catch(() => {
    // Allow a later hover/click to retry after a transient failure.
    warmed.delete(factoryKey);
  });
}

/** Test-only: clear the warmed set between cases. */
export function resetRoutePrefetchForTests(): void {
  warmed.clear();
}

/** Test-only: expose which prefixes have been warmed. */
export function warmedRoutePrefixesForTests(): ReadonlySet<string> {
  return warmed;
}
